import { Node as PmNode, type ResolvedPos } from 'prosemirror-model'
import { type Command, TextSelection } from 'prosemirror-state'

import { isStartOfPage } from '#choo-app/lib/editor/plugins/pagination'

// TODO: we should export this from codex
const PAGE_BREAK_TYPE = {
  manual: 'manual',
  dynamic: 'dynamic',
}

const isManualPageBreak = (node: PmNode) => {
  return node.attrs.pageBreak === PAGE_BREAK_TYPE.manual
}

// When we create a page break, we do not split the block or delete selections,
// instead we put a page break above the block.
const create: Command = (editorState, dispatch) => {
  const { $from } = editorState.selection

  const targetBlock = $from.node(2)
  const beforePos = $from.before(2)
  const startBlockOfPage = isStartOfPage(editorState, beforePos)

  if (dispatch) {
    const { tr } = editorState
    tr.setNodeMarkup(beforePos, undefined, {
      ...targetBlock.attrs,
      pageBreak: PAGE_BREAK_TYPE.manual,
    })
    // if the target block is the first block of a page,
    // we need to insert a new block above it so that current page's content is not empty
    if (startBlockOfPage) {
      // if the target block already has a manual page break, and the block is the start of the page,
      // we need to insert a new block above it with pageBreak attr
      if (isManualPageBreak(targetBlock)) {
        tr.insert(
          beforePos,
          targetBlock.type.create({ pageBreak: PAGE_BREAK_TYPE.manual }),
        )
      } else {
        tr.insert(beforePos, targetBlock.type.create())
      }
    }

    dispatch(tr.scrollIntoView())
  }

  return true
}

const remove: Command = (editorState, dispatch) => {
  const { selection } = editorState
  const { $from } = selection
  const targetBlock = $from.node(2)
  const pos = $from.start(2)

  const shouldRemovePageBreak =
    selection instanceof TextSelection &&
    selection.$cursor &&
    selection.$cursor.parentOffset === 0 &&
    isManualPageBreak(targetBlock)

  if (!shouldRemovePageBreak) {
    return false
  }

  if (dispatch) {
    const { tr } = editorState
    tr.setNodeMarkup(pos - 1, undefined, {
      ...targetBlock.attrs,
      pageBreak: null,
    })
    dispatch(tr.scrollIntoView())
  }

  return true
}

// not exported from prosemirror-commands
const findCutBefore = ($pos: ResolvedPos): ResolvedPos | null => {
  if (!$pos.parent.type.spec.isolating) {
    for (let i = $pos.depth - 1; i >= 0; i--) {
      if ($pos.index(i) > 0) return $pos.doc.resolve($pos.before(i + 1))
      if ($pos.node(i).type.spec.isolating) break
    }
  }
  return null
}

// When backspacing at the start of a block, the default joinBackward deletes
// an empty previous block without transferring its attrs. This command
// intercepts that case when the empty prev block carries a pageBreak attr,
// deleting it and applying the attr to the current block instead.
export const backspacePreservePageBreak: Command = (state, dispatch) => {
  if (!(state.selection instanceof TextSelection)) return false
  const { $cursor } = state.selection
  if (!$cursor || $cursor.parentOffset !== 0) return false

  const $cut = findCutBefore($cursor)
  if (!$cut) return false

  const before = $cut.nodeBefore
  if (!before || before.content.size !== 0) return false

  const { pageBreak } = before.attrs
  if (!pageBreak) return false

  if (dispatch) {
    const { tr } = state
    tr.delete($cut.pos - before.nodeSize, $cut.pos)
    tr.setNodeMarkup(tr.mapping.map($cut.pos), undefined, {
      ...$cursor.parent.attrs,
      pageBreak,
    })
    dispatch(tr.scrollIntoView())
  }
  return true
}

export const pageBreak = {
  create,
  remove,
}
